'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from '@/components/Icons/Icons';

interface ActionButton {
  label: string;
  onClick: () => void;
  variant?: 'PrimaryFillButton' | 'SecondaryFillButton';
  icon?: React.ReactNode;
}

interface CourseCompletedScreenProps {
  title: string;
  completionMessage?: string;
  subtitle?: string;
  primaryAction?: ActionButton;
  secondaryAction?: ActionButton;
  showGlow?: boolean;
  onClose?: () => void;
  enableFeedbackNavigation?: boolean;
}

const navigateToFeedbackTab = () => {
  setTimeout(() => {
    const feedbackTab = Array.from(
      document.querySelectorAll('nav[aria-label="Tabs"] button')
    ).find((btn) => btn.textContent?.trim() === 'Feedback') as HTMLButtonElement;
    
    if (feedbackTab) {
      feedbackTab.click();
      setTimeout(() => {
        feedbackTab.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, 300);
};

export default function CourseCompletedScreen({
  title,
  completionMessage = 'You have completed',
  subtitle,
  primaryAction,
  secondaryAction,
  showGlow = true,
  onClose,
  enableFeedbackNavigation = false,
}: CourseCompletedScreenProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handlePrimaryClick = () => {
    if (primaryAction?.onClick) {
      primaryAction.onClick();
    }
    if (enableFeedbackNavigation) {
      navigateToFeedbackTab();
    }
  };

  const defaultPrimaryAction: ActionButton = primaryAction || {
    label: 'Continue',
    onClick: onClose || (() => { }),
    variant: 'PrimaryFillButton',
  };

  return (
    <div
      className={`absolute inset-0 z-[2] flex flex-col items-center justify-center gap-5 bg-[var(--Surfaces-Overlay-Sheen-75,rgba(0,0,0,0.75))] p-2 backdrop-blur-lg transition-opacity duration-500 sm:gap-6 sm:p-3 md:gap-8 md:p-4 ${isVisible ? 'opacity-100' : 'opacity-0'
        }`}
    >
      {showGlow && <div className="course-completed-glow"></div>}

      <div
        className={`flex w-[90%] max-w-[900px] flex-col items-center gap-6 px-3 transition-all duration-700 sm:gap-8 sm:px-4 md:px-5 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
      >
        <div className="flex w-full flex-col items-center gap-4 sm:gap-6">
          <h4 className="cs-h4 text-center text-[var(--Color-Extended-Amethyst-Amethyst-Accent)]">
            {completionMessage}
          </h4>

          <h1 className="cs-h1 px-2 text-center text-[var(--Text-Default-Strong)]">
            {title}
          </h1>

          {subtitle && (
            <p className="cs-m-body text-center text-[var(--Text-Default-Light)]">
              {subtitle}
            </p>
          )}
        </div>

        {(secondaryAction || primaryAction) && (
          <div className="flex w-full max-w-[600px] flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
            {secondaryAction && (
              <Button
                onClick={secondaryAction.onClick}
                variant={secondaryAction.variant || 'SecondaryFillButton'}
                className="w-full sm:w-auto"
              >
                {secondaryAction.label}
                {secondaryAction.icon || (
                  <ArrowRight className="text-[var(--Text-Default-Strong)]" fillOpacity="0.67" width={12} height={10}  />
                )}
              </Button>
            )}

            {primaryAction && (
              <Button
                onClick={handlePrimaryClick}
                variant={primaryAction.variant || 'PrimaryFillButton'}
                className="w-full sm:w-auto"
              >
                {primaryAction.label}
                {primaryAction.icon || (
                  <ArrowRight className="text-[var(--Text-Inverse-Strong)]" fillOpacity="0.67" width={12} height={10}  />
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
